import {
  WSCard,
  WSElement,
  WSEmptyState,
  WSFlexBox
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { BadgePayoutPreference } from "../BadgePayoutPreference";
import {
  PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD,
  PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD
} from "../../../Settings/paths";
import { LabelMethod } from "../LabelMethod";
import { MethodType } from "../ManageMethods/types";
import styles from "./DefaultPayoutMethod.module.scss";

type Props = { noDescription?: boolean };

export const DefaultPayoutMethod: React.FC<Props> = ({ noDescription }) => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const history = useHistory();
  const { method, isLoading } = useDefaultPayoutMethod();

  return (
    <WSCard
      divider={!noDescription}
      header={{
        label: {
          text: "Payout method",
          helperText: noDescription
            ? undefined
            : "Default method to receive payments from clients"
        },
        value: noDescription
          ? undefined
          : {
              pill: {
                theme: "neutral",
                text: "Default",
                badge: true
              }
            }
      }}
      actions={{
        alignment: "left",
        buttons: [
          {
            kind: "Link",
            label: "Manage payout method",
            rightIcon: "chevron-right",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD);
            }
          }
        ]
      }}
    >
      {isLoading ? (
        <WSElement shimmer className={styles.loadingMethod} />
      ) : method ? (
        <WSFlexBox justify="space-between" alignItems="center" wrap="nowrap">
          <LabelMethod type={method.type} id={method.id} />
          {queryPayoutSettings.data?.preferredPayoutSpeed && (
            <BadgePayoutPreference
              payoutPreference={queryPayoutSettings.data.preferredPayoutSpeed}
            />
          )}
        </WSFlexBox>
      ) : (
        <WSEmptyState
          type="card"
          title="No payout method added"
          orientation="horizontal"
          description="Add a default payout method to get paid."
          size="S"
          actions={{
            buttons: [
              {
                label: "Add payout method",
                kind: "Secondary",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }
              }
            ]
          }}
        />
      )}
    </WSCard>
  );
};

export function payoutDestinationTypeToMethodType(
  destinationType?: PayoutDestinationType
): MethodType {
  switch (destinationType) {
    case PayoutDestinationType.EXTERNAL_BANK_ACCOUNT:
      return "Account";
    case PayoutDestinationType.PAYMENT_CARD:
      return "PaymentCard";
    case PayoutDestinationType.INTERNAL_ACCOUNT:
      return "InternalAccount";
    case PayoutDestinationType.GIFT_CARD:
      return "GiftCard";
    default:
      return "Historical";
  }
}

type PayoutMethod = {
  type: MethodType;
  id: string;
  category: PreferredPayoutSpeed;
};

/**
 * The account's default payout method, plus whether that answer is known yet.
 *
 * `isLoading` matters because "no method" is a two-read conclusion: the payout
 * settings may name no destination and still resolve to the Wallet, which only
 * the internal-accounts list can rule out. Callers must render neither the
 * method nor an empty state while it is true — a shimmer only recolours the
 * text, so an "add a payout method" call to action rendered underneath one is
 * still readable, focusable and clickable.
 */
export const useDefaultPayoutMethod = (): {
  method: PayoutMethod | undefined;
  isLoading: boolean;
} => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  return useMemo(() => {
    const payoutSettings = queryPayoutSettings.data;

    if (!payoutSettings) {
      return { method: undefined, isLoading: queryPayoutSettings.isLoading };
    }

    const category =
      payoutSettings.preferredPayoutSpeed || PreferredPayoutSpeed.STANDARD;
    const destination =
      selectorPayoutSettingsDefaultDestination(payoutSettings);

    // If no payout destination is set, check if there is Wallet
    if (!destination) {
      const bankingInternalAccount = queryInternalAccounts.data?.data?.find(
        selectorActiveBankingAccount
      );

      if (!bankingInternalAccount) {
        return {
          method: undefined,
          isLoading: queryInternalAccounts.isLoading
        };
      }

      return {
        method: {
          type: "InternalAccount" as MethodType,
          id: bankingInternalAccount.id,
          category
        },
        isLoading: false
      };
    }

    return {
      method: {
        type: payoutDestinationTypeToMethodType(destination.type),
        id: destination.id ?? "",
        category
      },
      isLoading: false
    };
  }, [
    queryInternalAccounts.data,
    queryInternalAccounts.isLoading,
    queryPayoutSettings.data,
    queryPayoutSettings.isLoading
  ]);
};
